import * as THREE from 'three';
import type { Vec3 } from '../../sim/mathx';
import {
  SCOUT_CAMERA_CONFIG,
  isPointInFrustum,
  scoutCameraPose,
  type ScoutCameraState,
} from '../../sim/vision';
import { FLU_TO_THREE_CAMERA } from './CameraRig';

export interface ObservationOverlayData {
  crosshair: Readonly<{ x: 0; y: 0 }>;
  projectedTarget: Readonly<Vec3>;
  box: {
    draw: boolean;
    targetNdc: Readonly<Vec3>;
  };
}

const CROSSHAIR = Object.freeze({ x: 0 as const, y: 0 as const });

/** Three camera counterpart of the simulation's shared observation sensor. */
export class ScoutObservationCamera {
  readonly camera: THREE.PerspectiveCamera;
  readonly crosshair = CROSSHAIR;

  constructor() {
    this.camera = new THREE.PerspectiveCamera(
      SCOUT_CAMERA_CONFIG.fovDeg,
      SCOUT_CAMERA_CONFIG.aspect,
      SCOUT_CAMERA_CONFIG.near,
      SCOUT_CAMERA_CONFIG.far,
    );
    this.camera.up.set(0, 0, 1);
  }

  update(scoutState: ScoutCameraState): void {
    const pose = scoutCameraPose(scoutState, SCOUT_CAMERA_CONFIG.tiltDeg);
    this.camera.position.set(pose.position.x, pose.position.y, pose.position.z);
    this.camera.quaternion
      .set(pose.orientation.x, pose.orientation.y, pose.orientation.z, pose.orientation.w)
      .normalize()
      .multiply(FLU_TO_THREE_CAMERA)
      .normalize();
    this.camera.updateMatrixWorld(true);
  }

  targetInView(scoutState: ScoutCameraState, targetEnu: Vec3): boolean {
    return isPointInFrustum(
      scoutCameraPose(scoutState),
      SCOUT_CAMERA_CONFIG.fovDeg,
      SCOUT_CAMERA_CONFIG.aspect,
      SCOUT_CAMERA_CONFIG.near,
      SCOUT_CAMERA_CONFIG.far,
      targetEnu,
    );
  }

  /** Returns normalized-device coordinates for the app-owned PiP overlay. */
  overlayForTarget(scoutState: ScoutCameraState, targetEnu: Vec3): ObservationOverlayData {
    this.update(scoutState);
    const projected = new THREE.Vector3(targetEnu.x, targetEnu.y, targetEnu.z).project(this.camera);
    const projectedTarget = { x: projected.x, y: projected.y, z: projected.z };
    return {
      crosshair: this.crosshair,
      projectedTarget,
      box: {
        draw: this.targetInView(scoutState, targetEnu),
        targetNdc: projectedTarget,
      },
    };
  }
}
